import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

interface PublicNavProps {
  activePage?: 'features' | 'pricing' | 'blog';
}

export default function PublicNav({ activePage }: PublicNavProps): React.ReactElement {
  const navigate = useNavigate();
  const [isProductsOpen, setIsProductsOpen] = useState(false);
  const productsRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (productsRef.current && !productsRef.current.contains(event.target as Node)) {
        setIsProductsOpen(false);
      }
    }
    if (isProductsOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isProductsOpen]);

  return (
    <nav className="bg-white border-b border-gray-200 sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Logo */}
          <div className="flex items-center cursor-pointer" onClick={() => navigate('/')}>
            <img src="/logo1.png" alt="Nivoon" className="h-16" />
          </div>

          {/* Navigation Links */}
          <div className="hidden md:flex items-center space-x-8">
            {/* Products Dropdown */}
            <div className="relative" ref={productsRef}>
              <button
                onClick={() => setIsProductsOpen(!isProductsOpen)}
                className="flex items-center text-gray-700 hover:text-indigo-600 transition-colors"
              >
                Products
                <svg
                  className={`ml-1 w-4 h-4 transition-transform ${isProductsOpen ? 'rotate-180' : ''}`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              {isProductsOpen && (
                <div className="absolute left-0 mt-2 w-80 rounded-lg shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-50">
                  <div className="py-2">
                    <a
                      href="/#ai-requirements"
                      onClick={(e) => {
                        e.preventDefault();
                        setIsProductsOpen(false);
                        if (window.location.pathname === '/') {
                          document.getElementById('ai-requirements')?.scrollIntoView({ behavior: 'smooth' });
                        } else {
                          navigate('/');
                          setTimeout(() => {
                            document.getElementById('ai-requirements')?.scrollIntoView({ behavior: 'smooth' });
                          }, 100);
                        }
                      }}
                      className="flex items-start px-4 py-3 hover:bg-gray-50 transition-colors"
                    >
                      <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-gradient-to-r from-indigo-600 to-blue-600 flex items-center justify-center mr-3">
                        <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                        </svg>
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-gray-900">Intelligent Requirements Management</p>
                        <p className="text-xs text-gray-500 mt-0.5">AI-powered platform for requirements creation, tracking, and collaboration</p>
                      </div>
                    </a>
                    <a
                      href="/construction"
                      onClick={(e) => {
                        e.preventDefault();
                        setIsProductsOpen(false);
                        navigate('/construction');
                      }}
                      className="flex items-start px-4 py-3 hover:bg-gray-50 transition-colors"
                    >
                      <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-gradient-to-r from-amber-500 to-orange-600 flex items-center justify-center mr-3">
                        <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                        </svg>
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-gray-900">Nivoon for Construction</p>
                        <p className="text-xs text-gray-500 mt-0.5">Construction workflow and project management platform</p>
                      </div>
                    </a>
                  </div>
                </div>
              )}
            </div>

            {activePage === 'features' ? (
              <span className="text-indigo-600 font-medium">Features</span>
            ) : (
              <a href="/features" className="text-gray-700 hover:text-indigo-600 transition-colors">Features</a>
            )}
            {activePage === 'pricing' ? (
              <span className="text-indigo-600 font-medium">Pricing</span>
            ) : (
              <a href="/pricing" className="text-gray-700 hover:text-indigo-600 transition-colors">Pricing</a>
            )}
            {activePage === 'blog' ? (
              <span className="text-indigo-600 font-medium">Blog</span>
            ) : (
              <a href="/blog" className="text-gray-700 hover:text-indigo-600 transition-colors">Blog</a>
            )}
          </div>

          {/* CTA Buttons */}
          <div className="flex items-center space-x-4">
            <button
              onClick={() => navigate('/login')}
              className="text-gray-700 hover:text-indigo-600 font-medium transition-colors"
            >
              Sign In
            </button>
            <button
              onClick={() => navigate('/register')}
              className="px-6 py-2 bg-gradient-to-r from-indigo-600 to-blue-600 text-white font-medium rounded-md hover:from-indigo-700 hover:to-blue-700 transition-all shadow-md hover:shadow-lg"
            >
              Get Started
            </button>
          </div>
        </div>
      </div>
    </nav>
  );
}
